import {
  HiristArrowDisabled,
  HiristArrowEnabled,
} from '@jobseeker/lotus/assets/icons';
import {Box} from '@jobseeker/lotus/foundations';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {Button, Grid, IconButton, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import PickerWithButtonField from './PickerWithButtonField';
import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {interviewService} from '@jobseeker/common-core/services';
import SlotTimingConfirmationDialog from './SlotTimingConfirmationDialog';
// import SlotTimingSelectionDialog from './SlotTimingSelectionDialog';

const getMonthName = (date: Date) => {
  return dayjs(date).format('MMM');
};

const InterviewDatePicker = ({
  fromDate,
  toDate,
  slotId,
  calendarId,
  reschedule = 0,
  handleDateChange,
  recruiterId = 0,
}: {
  fromDate: Date;
  toDate: Date;
  calendarId: string;
  slotId: string;
  handleDateChange: (date: Date) => void;
  reschedule?: number;
  recruiterId?: number;
}) => {
  const theme = useWrappedTheme();
  const today = new Date();
  const isAboveLarge = useIsLargeDevice('md');

  const [selectedDate, setSelectedDate] = useState(fromDate);
  const [selectedTiming, setSelectedTiming] = useState('');
  const [
    showSlotTimingConfirmationDialog,
    setShowSlotTimingConfirmationDialog,
  ] = useState(false);
  // const [showSlotTimingSelectionDialog, setShowSlotTimingSelectionDialog] =
  //   useState(false);
  const [slotsTimingData, setSlotsTimingData] = useState<{
    [date: string]: string[];
  }>({});
  // const [slotTimings, setSlotTimings] = useState<string[]>([]);

  const [sortedDates, setSortedDates] = useState<string[]>([]);

  useEffect(() => {
    const adjustedDate = dayjs(fromDate).toISOString();

    (async () => {
      const data = await interviewService.getSlotTimings({
        calendarId,
        slotId,
        date: adjustedDate,
        toDate: toDate.toISOString(),
      });

      if (!data || !data?.data || data?.error) {
        return;
      }

      setSlotsTimingData(data.data.slotsData);
      setSortedDates(Object.keys(data.data.slotsData));
    })();
  }, [fromDate, toDate, calendarId, slotId]);

  const showPastCalendar = () => {
    handleDateChange?.(
      dayjs(fromDate)
        .subtract(isAboveLarge ? 6 : 2, 'days')
        .toDate()
    );
  };

  const showFutureCalendar = () => {
    handleDateChange?.(
      dayjs(fromDate)
        .add(isAboveLarge ? 7 : 3, 'days')
        .startOf('day')
        .toDate()
    );
  };

  const handleSlotTimingConfirmationDialogClose = () => {
    setShowSlotTimingConfirmationDialog(false);
  };

  // const handleSlotTimingSelectionDialogClose = () => {
  //   setShowSlotTimingSelectionDialog(false);
  // };

  return (
    <>
      <SlotTimingConfirmationDialog
        calendarId={calendarId}
        slotId={slotId}
        date={selectedDate}
        timing={selectedTiming}
        open={showSlotTimingConfirmationDialog}
        reschedule={reschedule}
        handleSlotTimingConfirmationDialogClose={
          handleSlotTimingConfirmationDialogClose
        }
        recruiterId={recruiterId}
      />
      {/* <SlotTimingSelectionDialog
        calendarId={calendarId}
        slotId={slotId}
        date={selectedDate}
        slotTimings={slotTimings}
        open={showSlotTimingSelectionDialog}
        handleSlotTimingSelectionDialogClose={
          handleSlotTimingSelectionDialogClose
        }
      /> */}
      <Box
        px={isAboveLarge ? 6 : 2}
        py={isAboveLarge ? 1.75 : 0}
        mb={1}
        mt={{xxs: 2.5, md: 3.5}}
        display="flex"
        justifyContent="center"
      >
        <Box
          justifyContent="space-between"
          display="flex"
          alignItems="center"
          flexDirection="row"
          gap={1}
          width="100%"
        >
          <Typography
            sx={{
              fontSize: theme.typography.pxToRem(isAboveLarge ? 18 : 14),
              fontWeight: 500,
            }}
            lineHeight={'20px'}
          >
            Please select interview slot from below.
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <PickerWithButtonField handleDateChange={handleDateChange} />
            {isAboveLarge && (
              <Typography
                textAlign="center"
                lineHeight="20px"
                variant="subtitle1"
              >
                {dayjs(fromDate).year() === dayjs(toDate).year()
                  ? `${dayjs(fromDate).date()} ${getMonthName(
                      fromDate
                    )} - ${dayjs(toDate).date()} ${getMonthName(
                      toDate
                    )}, ${dayjs(toDate).year()}`
                  : `${dayjs(fromDate).date()} ${getMonthName(
                      fromDate
                    )}, ${dayjs(fromDate).year()} - ${dayjs(
                      toDate
                    ).date()} ${getMonthName(toDate)}, ${dayjs(
                      toDate
                    ).year()}`}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Grid container width="100%" px={isAboveLarge ? 1.5 : 1}>
        <Grid
          item
          alignSelf="start"
          xs={0.3}
          position="relative"
          left={isAboveLarge ? 20 : 0}
        >
          <IconButton
            onClick={showPastCalendar}
            sx={{
              p: 0,
              zIndex: 1,
              height: '100%',
              mt: 4,
              ...(fromDate > today && {transform: 'rotate(180deg)'}),
            }}
            disabled={fromDate <= today}
            disableRipple
          >
            <Image
              src={fromDate <= today ? HiristArrowDisabled : HiristArrowEnabled}
              alt="left"
              height={isAboveLarge ? 35 : 30}
              width={isAboveLarge ? 35 : 30}
            />
          </IconButton>
        </Grid>
        <Grid item xs={11.4}>
          <Grid container bgcolor="white" textAlign="center">
            {sortedDates?.slice(0, isAboveLarge ? 6 : 2)?.map(date => {
              return (
                <>
                  <Grid
                    xs={isAboveLarge ? 2 : 6}
                    item
                    borderRight="1px solid #f2f5fa"
                  >
                    <Button
                      variant="text"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderBottom: '1px solid #f2f5fa',
                        width: '100%',
                        ':hover': {
                          cursor: 'auto',
                          bgcolor: 'transparent',
                        },
                      }}
                      disableRipple
                      // onClick={() => {
                      //   setSelectedDate(new Date(date));
                      //   setSlotTimings(slotsTimingData[date]);

                      //   setShowSlotTimingSelectionDialog(true);
                      // }}
                    >
                      <Typography
                        color={
                          dayjs(date).isSame(dayjs(), 'day')
                            ? 'rgb(233, 99, 12)'
                            : 'black'
                        }
                        fontSize={theme.typography.pxToRem(
                          isAboveLarge ? 24 : 20
                        )}
                        fontWeight={600}
                      >
                        {dayjs(date).format('DD-MM-YYYY')}
                      </Typography>
                      <Typography fontSize={theme.typography.pxToRem(12)}>
                        {dayjs(date).format('dddd').toUpperCase()}
                      </Typography>
                      <Box
                        bgcolor="#f2f5fa"
                        maxWidth={151}
                        px={2}
                        borderRadius={0.375}
                        mt={1.25}
                        mb={1.25}
                        textAlign="center"
                      >
                        <Typography
                          fontSize={theme.typography.pxToRem(10)}
                          color={theme.palette.primary.main}
                          fontWeight={700}
                        >
                          {slotsTimingData[date].length} AVAILABLE SLOT
                          {slotsTimingData[date].length === 1 ? '' : 'S'}
                        </Typography>
                      </Box>
                    </Button>
                    {slotsTimingData[date].map((slotTiming, index) => (
                      <Box
                        key={index}
                        borderBottom="1px solid #f2f5fa"
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        px={1.875}
                        py={1.5}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            borderRadius: 0.625,
                            bgcolor: '#e8fff0',
                            color: '#2bae65',
                            fontSize: 13,
                            fontWeight: 400,
                            py: 1.5,
                            width: '100%',
                            ':hover': {
                              bgcolor: '#e8fff0',
                            },
                            height: 38,
                          }}
                          onClick={() => {
                            setSelectedDate(new Date(date));
                            setSelectedTiming(slotTiming);

                            setShowSlotTimingConfirmationDialog(true);
                          }}
                          data-testid="slot-time-button"
                        >
                          {slotTiming}
                        </Button>
                      </Box>
                    ))}
                  </Grid>
                </>
              );
            })}
          </Grid>
        </Grid>
        <Grid item alignSelf="start" xs={0.3} position="relative" right={20}>
          <IconButton
            onClick={showFutureCalendar}
            sx={{
              p: 0,
              height: '100%',
              mt: 4,
              ...(dayjs(toDate).add(1, 'day').diff(dayjs(), 'days') > 30 && {
                transform: 'rotate(180deg)',
                cursor: 'not-allowed !important',
              }),
            }}
            disabled={dayjs(toDate).add(1, 'day').diff(dayjs(), 'days') > 30}
            disableRipple
          >
            <Image
              src={
                dayjs(toDate).add(1, 'day').diff(dayjs(), 'days') > 30
                  ? HiristArrowDisabled
                  : HiristArrowEnabled
              }
              alt="right"
              height={isAboveLarge ? 35 : 30}
              width={isAboveLarge ? 35 : 30}
            />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
};

export default InterviewDatePicker;
